/* CreateBookingSheet · Feb 2026
 * ============================================================
 * Extracted from InstructorDashboard.js's `activePanel === "new-lesson"`
 * MobileDialog. The founder's proven canonical booking-creation UI.
 *
 * ONE component. N consumers (Today.js, InstructorDashboard.js,
 * future calendar surfaces).
 *
 * Contract (NDG-INTENT-CALENDAR-01):
 *   Props:
 *     open            : boolean
 *     onClose         : () => void
 *     onCreated       : (lesson) => void  — parent may refetch etc.
 *     date            : "YYYY-MM-DD"       — REQUIRED when open
 *     startTime       : "HH:MM"            — REQUIRED when open
 *     durationMinutes : number             — REQUIRED when open (canonical)
 *
 *   Guarantees:
 *     - date, startTime, durationMinutes are read AS PROPS. No URL
 *       round-trip. No query-string parsing. Intent is carried by
 *       React state.
 *     - endTime is derived as startTime + durationMinutes.
 *     - The confirm handler POSTs /api/lesson-events with the
 *       prefilled fields — identical to the original.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import { MobileDialog } from "../mobile";
import { Button } from "../ui/button";
import { timeToMin, minToTime } from "../../utils/calendar";
import AddPupilFlow from "../instructor/AddPupilFlow";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreateBookingSheet({
  open,
  onClose,
  onCreated,
  date,
  startTime,
  durationMinutes = 60,
  defaultAmount = "45",
  defaultPickup = "",
}) {
  const [pupils, setPupils] = useState([]);
  const [pupilQuery, setPupilQuery] = useState("");
  const [form, setForm] = useState({
    pupil_id: "", date: "", start_time: "", end_time: "",
    amount: "", pickup_location: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [addingPupil, setAddingPupil] = useState(false);

  /* Every time the sheet is opened with a slot, prefill from props.
   * Intent is preserved in React state — no URL indirection. */
  useEffect(() => {
    if (open && date && startTime) {
      const endMin = timeToMin(startTime) + (durationMinutes || 60);
      setForm({
        pupil_id: "",
        date,
        start_time: startTime,
        end_time: minToTime(endMin),
        amount: defaultAmount,
        pickup_location: defaultPickup,
        notes: "",
      });
    }
  }, [open, date, startTime, durationMinutes, defaultAmount, defaultPickup]);

  // Expose the open, unsaved scheduling draft to the universal intent bar.
  // These hints cannot create a lesson; they only allow a returned draft
  // command to change the visible form before the user presses Confirm.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!open) {
      delete window.__ndgBookingDraft;
      return undefined;
    }
    const draft = {
      date: form.date,
      start_time: form.start_time,
      end_time: form.end_time,
    };
    window.__ndgBookingDraft = draft;
    return () => {
      if (window.__ndgBookingDraft === draft) delete window.__ndgBookingDraft;
    };
  }, [open, form.date, form.start_time, form.end_time]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const applyDraftUpdate = (event) => {
      if (!open) return;
      const update = event?.detail || {};
      const timePattern = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
      if (!timePattern.test(update.start_time || "") || !timePattern.test(update.end_time || "")) {
        return;
      }
      setForm((current) => ({
        ...current,
        date: update.date || current.date,
        start_time: update.start_time,
        end_time: update.end_time,
      }));
      toast.success(`Booking time changed to ${update.start_time}–${update.end_time}`);
    };
    window.addEventListener("ndg:booking-draft-update", applyDraftUpdate);
    return () => window.removeEventListener("ndg:booking-draft-update", applyDraftUpdate);
  }, [open]);

  /* Booking is permitted for ACTIVE and WAITING pupils. Load both
   * deliberately so a new pupil cannot disappear before lesson one. */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.allSettled([
      axios.get(`${API}/api/pupils?lifecycle_status=all`, { withCredentials: true }),
      axios.get(`${API}/api/pupils`, { withCredentials: true }),
      axios.get(`${API}/api/work-mode/students?lifecycle_status=all`, { withCredentials: true }),
    ]).then((results) => {
      if (cancelled) return;
      const byId = new Map();
      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const payload = result.value.data;
        const candidates = Array.isArray(payload)
          ? payload
          : (payload?.pupils || payload?.students || []);
        candidates.forEach((pupil) => {
          const id = pupil.id || pupil.pupil_id || pupil.user_id || pupil._id;
          const fullName = (
            pupil.full_name
            || pupil.name
            || [pupil.first_name, pupil.last_name].filter(Boolean).join(" ")
            || "Unnamed pupil"
          ).trim();
          const normalised = { ...pupil, id: id ? String(id) : "", full_name: fullName };
          if (normalised.id) byId.set(normalised.id, normalised);
        });
      });
      setPupils([...byId.values()].sort((a, b) =>
        a.full_name.localeCompare(b.full_name)
      ));
    });
    return () => { cancelled = true; };
  }, [open]);

  const visiblePupils = pupils.filter((pupil) =>
    pupil.full_name.toLowerCase().includes(pupilQuery.trim().toLowerCase())
  );

  const onPupilCreated = (pupil) => {
    if (!pupil?.id) return;
    const normalised = {
      ...pupil,
      full_name: pupil.full_name || pupil.name || "Unnamed pupil",
      pickup_location:
        pupil.pickup_location ||
        pupil.primary_address ||
        pupil.default_pickup_address ||
        pupil.address ||
        "",
    };
    setPupils((current) => {
      const byId = new Map(current.map((item) => [item.id, item]));
      byId.set(normalised.id, normalised);
      return [...byId.values()].sort((a, b) =>
        a.full_name.localeCompare(b.full_name)
      );
    });
    setForm((current) => ({
      ...current,
      pupil_id: normalised.id,
      amount: normalised.default_lesson_price
        ? String(normalised.default_lesson_price)
        : current.amount,
      pickup_location: normalised.pickup_location || current.pickup_location,
    }));
  };

  const onPupilSelect = (pid) => {
    const p = pupils.find(pp => pp.id === pid);
    setForm({
      ...form,
      pupil_id: pid,
      amount: p?.default_lesson_price ? String(p.default_lesson_price) : form.amount,
      pickup_location: p?.pickup_location || form.pickup_location,
    });
  };

  const submit = async () => {
    if (!form.pupil_id) {
      toast.error("Choose a pupil first"); return;
    }
    if (!form.date || !form.start_time || !form.end_time) {
      toast.error("Date, start and end time required"); return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/lesson-events`, {
        ...form,
        amount: form.amount ? parseFloat(form.amount) : undefined,
      }, { withCredentials: true });
      toast.success("Lesson created");
      if (onCreated) onCreated(data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <>
    <MobileDialog bareContent open={open} onClose={onClose} testid="create-booking-sheet-wrap">
      <div className="bg-white dark:bg-slate-800 w-full max-w-lg rounded-t-3xl sm:rounded-3xl" onClick={e => e.stopPropagation()}>
        <div data-testid="create-booking-sheet" className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">Book Lesson</h2>
            <button data-testid="create-booking-close" onClick={onClose}
              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-500 font-bold mb-1.5 block">Pupil</label>
              <input
                data-testid="create-booking-pupil-search"
                type="search"
                value={pupilQuery}
                onChange={(e) => setPupilQuery(e.target.value)}
                placeholder="Type a pupil name, e.g. Louie"
                className="w-full mb-2 border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3 text-base bg-white text-slate-900 dark:bg-slate-700 dark:text-white"
              />
              <select
                data-testid="create-booking-pupil"
                value={form.pupil_id}
                onChange={e => onPupilSelect(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3.5 text-base font-medium bg-white text-slate-900 dark:bg-slate-700 dark:text-white"
              >
                <option value="">Select pupil...</option>
                {visiblePupils.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
              <button
                type="button"
                data-testid="create-booking-add-pupil"
                onClick={() => setAddingPupil(true)}
                className="text-sm text-blue-600 font-bold mt-2 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add pupil
              </button>
              {pupils.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Add your first pupil, then they will be selected here automatically.
                </p>
              )}
            </div>

            <div>
              <label className="text-sm text-slate-500 font-bold mb-1.5 block">Date *</label>
              <input
                data-testid="create-booking-date"
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3.5 text-base font-medium"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-500 font-bold mb-1.5 block">Start Time *</label>
                <input
                  data-testid="create-booking-start"
                  type="time" step="60"
                  value={form.start_time}
                  onChange={e => setForm({ ...form, start_time: e.target.value })}
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3.5 text-base font-medium"
                />
              </div>
              <div>
                <label className="text-sm text-slate-500 font-bold mb-1.5 block">End Time *</label>
                <input
                  data-testid="create-booking-end"
                  type="time" step="60"
                  value={form.end_time}
                  onChange={e => setForm({ ...form, end_time: e.target.value })}
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3.5 text-base font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-500 font-bold mb-1.5 block">Price (£)</label>
                <input
                  data-testid="create-booking-amount"
                  type="number"
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3.5 text-base font-medium"
                  placeholder="£"
                />
              </div>
              <div>
                <label className="text-sm text-slate-500 font-bold mb-1.5 block">Location</label>
                <input
                  data-testid="create-booking-location"
                  type="text"
                  value={form.pickup_location}
                  onChange={e => setForm({ ...form, pickup_location: e.target.value })}
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3.5 text-base font-medium"
                />
              </div>
            </div>

            <Button
              data-testid="create-booking-confirm"
              onClick={submit}
              disabled={submitting || !form.pupil_id}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-base font-extrabold rounded-xl mt-2"
            >
              {submitting ? "Creating..." : "Confirm Lesson"}
            </Button>
          </div>
        </div>
      </div>
    </MobileDialog>
    <AddPupilFlow
      open={addingPupil}
      onClose={() => setAddingPupil(false)}
      onCreated={onPupilCreated}
    />
    </>
  );
}
